import React, { useState, useEffect, useMemo } from "react";
import EmailList from "./EmailList";
import JiraIssueTable from "./JiraIssueTable";
import { functionResponsesRequiringApproval } from "../../utils/constants";
import DynamicJiraIssueTable from "./DynamicJiraIssueTable";
import CalendarEventEditor from "../meeting/CalendarEventEditor";
import { DataState } from "../../context/DataProvider";
import DynamicTable from "./DynamicTable";
import FileOperationOutput from "./FileOperationOutput";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";


const ToolsResponseUI = ({ tool, conversations }) => {
    const [contentParsed, setContentParsed] = useState(null);
    const [showOutput, setShowOutput] = useState(false);
    const { currentToolResponseForReference } = DataState();

    useEffect(() => {
        if (
            (!tool?.functionName || !functionResponsesRequiringApproval.has(tool.functionName)) &&
            !tool.ui_table_plan?.is_table_displayable
        ) {
            setContentParsed(null);
            return;
        }

        let rawResponseParsed = {};
        try {
            if (tool.display_data) {
                rawResponseParsed = tool.display_data;
            } else {
                rawResponseParsed = JSON.parse(tool.content || "{}");
            }
            setContentParsed(rawResponseParsed?.tool_response || rawResponseParsed);
        } catch (err) {
            console.error("Error parsing tool content:", err);
            setContentParsed({ error: "Failed to parse tool response." });
        }
    }, [tool]);

    const findOriginalToolCall = () => {
        if (!tool?.tool_call_id || !conversations) return null;
        for (let i = conversations.length - 1; i >= 0; i--) {
            const msg = conversations[i];
            if (msg.role === "assistant" && msg.tool_calls) {
                const originalCall = msg.tool_calls.find(
                    (tc) => tc.id === tool.tool_call_id
                );
                if (originalCall) return originalCall;
            }
        }
        return null;
    };

    const originalToolCall = useMemo(findOriginalToolCall, [tool.tool_call_id, conversations]);

    const parsedArguments = useMemo(() => {
        if (originalToolCall?.function?.arguments) {
            try {
                return JSON.parse(originalToolCall.function.arguments);
            } catch (e) {
                console.error("Failed to parse original tool call arguments:", e);
                return null;
            }
        }
        return null;
    }, [originalToolCall]);

    // --- RENDER LOGIC ---

    // **RENDER PATH 1: Calendar Event CREATION (ENHANCED)**
    const isSuccessfulCalendarCreation =
        ["create_calendar_event_outlook", "create_google_calendar_event"].includes(
            tool.functionName
        ) && contentParsed && !contentParsed.error;

    if (isSuccessfulCalendarCreation) {
        // --- ENHANCED DATA MAPPING ---
        const data = contentParsed;
        const finalEventData = {
            title: data.summary || data.title || data.subject || "Untitled Event",
            start: data.start?.dateTime || data.start?.time || data.startTime,
            end: data.end?.dateTime || data.end?.time || data.endTime,
            description: data.description || data.body?.content,
            location: data.location,
            attendees: data.attendees || [],
            // Captures the "Join" link for virtual meetings
            meetLink: data.hangoutLink || data.conferenceData?.entryPoints?.[0]?.uri || data.onlineMeeting?.joinUrl,
            // Captures the link to view the event in the calendar UI
            eventUrl: data.htmlLink || data.webLink, 
        };
        
        

        return (
            <div className="w-full pl-8 pr-4 sm:pl-12 sm-pr-6">
                <CalendarEventEditor
                    event={finalEventData}
                    approvalNeeded={false}
                    isReadOnly={true}
                />
            </div>
        );
    }

    // **RENDER PATH 2: Calendar Event UPDATE (ENHANCED)**
    const isSuccessfulCalendarUpdate =
        ["update_calendar_event", "update_google_calendar_event"].includes(
            tool.functionName
        ) && contentParsed && !contentParsed.error;

    if (isSuccessfulCalendarUpdate) {
       // --- ENHANCED DATA MAPPING ---
       const data = contentParsed;
       const finalEventData = {
           title: data.summary || data.title || data.subject || "Event Updated",
           start: data.start?.dateTime || data.start?.time || data.startTime,
           end: data.end?.dateTime || data.end?.time || data.endTime,
           description: data.description || data.body?.content,
           location: data.location,
           attendees: data.attendees || [],
           meetLink: data.hangoutLink || data.conferenceData?.entryPoints?.[0]?.uri || data.onlineMeeting?.joinUrl,
           eventUrl: data.htmlLink || data.webLink,
       };

        return (
            <div className="w-full pl-8 pr-4 sm:pl-12 sm:pr-6">
                <CalendarEventEditor event={finalEventData} approvalNeeded={false} isReadOnly={true} />
            </div>
        );
    }

    // **RENDER PATH 3: Fallback for all other tools**
    if (!contentParsed || contentParsed.error) {
        return null;
    }

    return (
        <>
            {["search_emails_on_gmail", "list_inbox_emails_on_gmail"].includes(
                tool.functionName
            ) ? (
                <EmailList emailList={contentParsed.emails || []} />
            ) : ["list_messages_outlook"].includes(tool.functionName) ? (
                <EmailList emailList={contentParsed.emails || []} isOutlook={true} />
            ) : ["search_issues_in_JIRA_independent_of_Agile"].includes(
                tool.functionName
            ) ? (
                <JiraIssueTable issues={contentParsed} />
            ) : ["advanced_jira_issue_search"].includes(tool.functionName) ? (
                <DynamicJiraIssueTable
                    issues={contentParsed.issues || contentParsed.query_result}
                />
            ) : tool.ui_table_plan?.is_table_displayable ? (
                <DynamicTable plan={tool.ui_table_plan} data={contentParsed} />
            ) : null}

            {/* Special case: performCRUDOperations... */}
            {tool.functionName ===
                "performCRUDOperationsOnUserFilesAndGenerateDataVisualizations" &&
                contentParsed["Standard Output"] ? (
                <div className="mt-0">
                    <button
                        className="flex items-center px-2 py-1 bg-transparent rounded-md hover:bg-zinc-800"
                        onClick={() => setShowOutput((prev) => !prev)}
                    >
                        {showOutput ? "Analysis" : "Analysis"}
                        {showOutput ? (
                            <FiChevronUp className="w-5 h-5 ml-2" />
                        ) : (
                            <FiChevronDown className="w-5 h-5 ml-2" />
                        )}
                    </button>
                    {showOutput && (
                        <div className="mt-0 ml-1 text-sm bg-transparent rounded-xl">
                            <div className="mt-0">
                                <FileOperationOutput
                                    output={contentParsed["Standard Output"]}
                                />
                            </div>
                        </div>
                    )}
                </div>
            ) : null}

            {tool.functionName !==
                "performCRUDOperationsOnUserFilesAndGenerateDataVisualizations" &&
                (
                    <div className="p-1 font-mono text-sm whitespace-pre-wrap ">
                        {contentParsed["Standard Output"]}
                    </div>
                )}
        </>
    );
};

export default ToolsResponseUI;